import type { ListBuyingOrdersInput } from '@erp/rpc';
import { buyingOrderReadiness, type OrderReadiness } from '@erp/shared-utils';
import { useBuyingOrders } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { useThresholdBufferPct } from '../utils/useThresholdBufferPct';
import type { SupplierAssignmentsQuery } from './supplierAssignmentsInput';

/**
 * The Orders-table query for one open cart (GEN-3234): the same line filters
 * as the board, kept to this vendor x ship-to. Order-level filters (search,
 * line types, readiness, days on order) are left off so the open cart's row is
 * always found. Null until a division scopes the board.
 */
export function cartOrderRowInput(
  query: SupplierAssignmentsQuery,
  shipToWarehouseId: string,
): ListBuyingOrdersInput | null {
  const { divisionIds } = query;
  if (!divisionIds || divisionIds.length === 0) return null;
  return {
    divisionIds,
    warehouseIds: query.warehouseIds,
    supplierIds: [query.supplierId],
    shipToWarehouseIds: [shipToWarehouseId],
    buyerCodes: query.buyerCodes,
    ...query.lineFilters,
    includeReleased: query.includeReleased,
    orderTypes: ['po'],
    groupBy: 'vendor_location',
    limit: 1,
  };
}

/**
 * The open cart's readiness, read from its Orders-table row through the one
 * shared rule, so the detail header and the Order column always agree. Null
 * while the row loads.
 */
export function useCartOrderReadiness(
  query: SupplierAssignmentsQuery,
  shipToWarehouseId: string,
): OrderReadiness | null {
  const input = cartOrderRowInput(query, shipToWarehouseId);
  const bufferPct = useThresholdBufferPct();
  const { data } = useBuyingOrders(
    input ?? { divisionIds: [], limit: 1 },
    input !== null,
  );
  const row = data?.rows.find(
    (r) =>
      r.supplierId === query.supplierId &&
      r.shipToWarehouseId === shipToWarehouseId,
  );
  return row ? buyingOrderReadiness(row, bufferPct) : null;
}
